import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../config/api";

const REVENUE_EXCLUDED_STATUSES = ["Cancelled", "Return Requested"];

function DashboardHome() {
  const [stats, setStats] = useState({
    parentCount: 0,
    schoolCount: 0,
    uniformCount: 0,
    pastCount: 0,
    newCount: 0,
    revenue: 0,
    recentOrders: [],
    revenueChart: [], 
  });
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", city: "", state: "", address: "",
  });
  const [editingId, setEditingId] = useState(null);

  const userRole = localStorage.getItem("role");
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const hasPermission = (key) => userRole === "admin" || permissions.includes(key);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dashboard/stats`),
        axios.get(`${API_BASE_URL}/orders/admin/orders`)
      ]);
      
      setStats(statsRes.data);
      setAllOrders(ordersRes.data || []); 
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const revenueOrders = allOrders.filter(
    (item) => !REVENUE_EXCLUDED_STATUSES.includes(item.status)
  );

  const totalRevenueCalculated = revenueOrders.reduce(
    (sum, item) => sum + (Number(item.totalAmount) || 0), 0
  );
  const totalRevenueToDisplay = allOrders.length > 0
    ? totalRevenueCalculated
    : (stats.revenue || 0);

  const resetForm = () => {
    setFormData({ name: "", phone: "", email: "", city: "", state: "", address: "" });
    setEditingId(null);
  };

  const chartDataToDisplay = stats.revenueChart && stats.revenueChart.length > 0 
    ? stats.revenueChart 
    : [];

  const pieData = [
    { name: "Paid Orders", value: stats.pastCount || 0 },
    { name: "New Orders", value: stats.newCount || 0 },
  ];
  const COLORS = ["#10b981", "#ef4444"];

  const allCards = [
    { label: "Total Parents", val: stats.parentCount, icon: "👨‍👩‍👧", bg: "#e0f2fe", path: "/dashboard/parents", key: "view_parents" },
    { label: "Active Schools", val: stats.schoolCount, icon: "🏫", bg: "#f3e8ff", path: "/dashboard/schools", key: "view_schools" },
    { label: "Total Products", val: stats.uniformCount, icon: "👕", bg: "#fff7ed", path: "/dashboard/product-detail", key: "view_products" },
    { label: "New Orders", val: stats.newCount, icon: "🆕", bg: "#fee2e2", path: "/dashboard/orders", key: "view_orders" },
    { label: "Total Revenue", val: "₹" + totalRevenueToDisplay.toLocaleString(), icon: "💰", bg: "#f0fdfa", path: "/dashboard/total-revenue", key: "view_revenue" },
  ];

  const visibleCards = allCards.filter((card) => hasPermission(card.key));

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = editingId
        ? await axios.put(`${API_BASE_URL}/schools/${editingId}`, formData)
        : await axios.post(`${API_BASE_URL}/schools`, formData);

      if (res.status === 200 || res.status === 201) {
        setShowModal(false);
        fetchStats();
        resetForm();
        Swal.fire({ icon: "success", title: "Saved!", timer: 2000, showConfirmButton: false });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Could not save school.",
      });
    }
  };

  if (loading) return <div className="p-5 text-center"><h4 className="text-muted">Loading Dashboard...</h4></div>;

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      {/* MODAL */}
      <AnimatePresence>
        {showModal && (
          <div className="custom-modal-overlay" onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="w-100" style={{ maxWidth: "600px" }} onClick={(e) => e.stopPropagation()}>
              <form onSubmit={handleSubmit} className="card card-body border-0 shadow-lg p-4 rounded-4" style={{ backgroundColor: "white" }}>
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                  <h5 className="fw-bold text-success m-0">{editingId ? "✏️ Update School" : "🏫 Register New School"}</h5>
                  <button type="button" className="btn btn-light rounded-circle" onClick={() => setShowModal(false)}><FaTimes /></button>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold text-muted">School Name</label>
                    <input name="name" className="form-control bg-light border-0 py-2"  placeholder="School name..." value={formData.name} onChange={handleChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold text-muted">Phone Number</label>
                    <input name="phone" type="text" maxLength="10" className="form-control bg-light border-0 py-2"  placeholder="Phone Number.." value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, "") })} required />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label small fw-bold text-muted">Email</label>
                    <input name="email" type="email" className="form-control bg-light border-0 py-2"  placeholder="xyz@gmail.com..." value={formData.email} onChange={handleChange} />
                  </div>
                   <div className="col-md-6">
                        <label className="form-label small fw-bold text-muted">
                          City
                        </label>
                        <input
                          name="city"
                          className="form-control bg-light border-0 py-2"
                          placeholder="City"
                          value={formData.city}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small fw-bold text-muted">
                          State
                        </label>
                        <input
                          name="state"
                          className="form-control bg-light border-0 py-2"
                          placeholder="State"
                          value={formData.state}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label small fw-bold text-muted">
                          Full Address
                        </label>
                        <textarea
                          name="address"
                          className="form-control bg-light border-0"
                          rows="2"
                          placeholder="Street address..."
                          value={formData.address}
                          onChange={handleChange}
                        ></textarea>
                      </div>
          
                  <div className="col-12 mt-4">
                    <button type="submit" className="btn btn-success w-100 py-2 fw-bold border-0" style={{ backgroundColor: "#097969" }}>{editingId ? "Update Record" : "Save School"}</button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="row">
        <div className="col-lg-9">
          {/* WELCOME BANNER */}
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="p-4 mb-4 shadow-sm text-white d-flex justify-content-between align-items-center" style={{ background: "linear-gradient(135deg, #097969 0%, #16a34a 100%)", borderRadius: "20px" }}>
            <div>
              <h6 className="text-uppercase mb-1" style={{ opacity: 0.8, letterSpacing: "1px" }}>Welcome back!</h6>
              <h2 className="fw-bold mb-3">{userRole === "admin" ? "Admin Overview 👋" : "Staff Overview 👋"}</h2>
              {hasPermission("view_schools") && (
                <div onClick={() => { resetForm(); setShowModal(true); }} className="hk-glow-link" style={{ cursor: "pointer" }}>
                  <div className="hk-glow-button"><span>Add School</span></div>
                </div>
              )}
            </div>
          </motion.div>

          {/* STATS CARDS */}
          <div className="row g-3 mb-4">
            {visibleCards.map((item, i) => (
              <div className="col-md-4 col-sm-6" key={i}>
                <Link to={item.path} style={{ textDecoration: "none", color: "inherit" }}>
                  <motion.div whileHover={{ y: -5, scale: 1.02 }} className="card border-0 shadow-sm p-4 rounded-4 h-100" style={{ background: item.bg }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="fw-bold mb-1" style={{ color: "#475569", fontSize: "12px", opacity: 0.8 }}>{item.label.toUpperCase()}</p>
                        <h3 className="fw-bold mb-0" style={{ color: "#1e293b" }}>{item.val}</h3>
                      </div>
                      <div className="fs-3 shadow-sm bg-white" style={{ width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "12px" }}>{item.icon}</div>
                    </div>
                  </motion.div>
                </Link>
              </div>
            ))}
          </div>

          <div className="row g-3">
            {/* REVENUE CHART */}
            {hasPermission("view_revenue") && (
              <div className="col-md-8">
                <div className="card border-0 shadow-sm p-4 rounded-4 h-100">
                  <h6 className="fw-bold mb-4 text-secondary">💰 Revenue Trend</h6>
                  <div style={{ height: 300, width: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartDataToDisplay}>
                        <defs>
                          <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#097969" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#097969" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="_id" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="revenue" stroke="#097969" strokeWidth={3} fill="url(#colorRev)" animationDuration={1000} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* PIE CHART */}
            {hasPermission("view_orders") && (
              <div className={hasPermission("view_revenue") ? "col-md-4" : "col-md-12"}>
                <div className="card border-0 shadow-sm p-4 rounded-4 h-100">
                  <h6 className="fw-bold mb-3 text-secondary">📦 Order Breakdown</h6>
                  <div style={{ height: 300, width: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} iconType="circle" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SIDEBAR */}
        <div className="col-lg-3 mt-4 mt-lg-0">
          <div className="card border-0 shadow-lg p-4 rounded-4 text-white" style={{ background: "#097969", height: "calc(100vh - 50px)", position: "sticky", top: "20px", overflowY: "auto" }}>
            <h5 className="fw-bold mb-4 border-bottom pb-2" style={{ borderColor: "rgba(255,255,255,0.2)" }}>🧾 Recent Orders</h5>
            {stats.recentOrders?.map((order) => (
              <div key={order._id} className="mb-3 p-3 rounded-3" style={{ background: "rgba(255,255,255,0.1)" }}>
                <p className="mb-1 fw-bold text-truncate small text-white">{order.parentName}</p>
                <div className="d-flex justify-content-between small">
                  <span className="text-white-50">{new Date(order.createdAt).toLocaleDateString()}</span>
                  <span className="fw-bold" style={{ color: "#edf9f5" }}>₹{order.totalAmount}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <style>{`
        .custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.4); backdrop-filter: blur(8px); display: flex; justify-content: center; align-items: center; z-index: 1050; }
        .hk-glow-button { --border-size: 2px; --btn-bg: #edf9f5; --accent-color: #097969; padding: 10px 25px; background: var(--btn-bg); border-radius: 10px; position: relative; display: flex; align-items: center; justify-content: center; min-width: 150px; height: 44px; box-shadow: 0 4px 10px rgba(9, 121, 105, 0.1); transition: all 0.3s ease; overflow: hidden; }
        .hk-glow-button::before { content: ""; position: absolute; top: 50%; left: 50%; width: 250%; height: 250%; background: conic-gradient(transparent 0%, transparent 15%, var(--accent-color) 20%, transparent 25%, transparent 100%); animation: spin linear 3s infinite; transform: translate(-50%, -50%); }
        .hk-glow-button::after { content: ""; position: absolute; top: var(--border-size); left: var(--border-size); right: var(--border-size); bottom: var(--border-size); background: var(--btn-bg); border-radius: 8px; z-index: 1; }
        .hk-glow-button span { position: relative; z-index: 2; font-size: 13px; font-weight: 700; color: var(--accent-color); }
        @keyframes spin { 0% { transform: translate(-50%, -50%) rotate(0deg); } 100% { transform: translate(-50%, -50%) rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default DashboardHome;
